import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ProfileHeader from './components/ProfileHeader';
import PersonalInformation from './components/PersonalInformation';
import AccountSettings from './components/AccountSettings';
import SecuritySettings from './components/SecuritySettings';
import NotificationSettings from './components/NotificationSettings';
import ActivityHistory from './components/ActivityHistory';

import Icon from '../../components/AppIcon';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  // Mock user data - in real app this would come from authentication context or API
  const [user, setUser] = useState({
    id: 'user-001',
    email: 'max.mustermann@example.com',
    firstName: 'Max',
    lastName: 'Mustermann',
    company: 'Vierdimensional GmbH',
    jobTitle: 'AI Solutions Architect',
    phone: '+49 30 12345678',
    location: 'Berlin, Deutschland',
    bio: 'Passionate about AI and machine learning technologies. Building intelligent solutions for modern businesses.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    joinedDate: '2024-01-15T10:30:00Z',
    lastLogin: '2025-01-05T09:45:00Z',
    emailVerified: true,
    twoFactorEnabled: true,
    apiKeysCount: 3,
    agentsCount: 6
  });

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated !== 'true') {
      navigate('/login');
      return;
    }

    // Set page title
    document.title = 'Profil - Vierdimensional';
  }, [navigate]);

  const tabs = [
    {
      id: 'personal',
      label: 'Persönliche Daten',
      icon: 'User',
      description: 'Grundlegende Profilinformationen'
    },
    {
      id: 'account',
      label: 'Konto-Einstellungen',
      icon: 'Settings',
      description: 'E-Mail, Passwort und Präferenzen'
    },
    {
      id: 'security',
      label: 'Sicherheit',
      icon: 'Shield',
      description: '2FA, API-Schlüssel und Sicherheit'
    },
    {
      id: 'notifications',
      label: 'Benachrichtigungen',
      icon: 'Bell',
      description: 'E-Mail und Push-Benachrichtigungen'
    },
    {
      id: 'activity',
      label: 'Aktivität',
      icon: 'Activity',
      description: 'Anmeldungen und Nutzungshistorie'
    }
  ];

  const handleUserUpdate = (updatedData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedData
    }));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalInformation user={user} onUpdate={handleUserUpdate} />;
      case 'account':
        return <AccountSettings user={user} onUpdate={handleUserUpdate} />;
      case 'security':
        return <SecuritySettings user={user} onUpdate={handleUserUpdate} />;
      case 'notifications':
        return <NotificationSettings user={user} onUpdate={handleUserUpdate} />;
      case 'activity':
        return <ActivityHistory user={user} />;
      default:
        return <PersonalInformation user={user} onUpdate={handleUserUpdate} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pb-16 lg:pb-0`}>
          <div className="p-6 max-w-6xl mx-auto">
            {/* Profile Header */}
            <ProfileHeader user={user} onUpdate={handleUserUpdate} />

            {/* Navigation Tabs */}
            <div className="mb-8">
              <div className="border-b border-border">
                <nav className="-mb-px flex space-x-8 overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        group flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                        ${activeTab === tab?.id 
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }
                      `}
                    >
                      <Icon 
                        name={tab?.icon} 
                        size={16} 
                        className={activeTab === tab?.id ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                      />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="animate-fade-in">
              {renderTabContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;